/**
 * change-secure-prompts-modal.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnInit } from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup, Validators }
  from "@angular/forms";
import { DialogRef, ModalComponent } from "angular2-modal";
import { BSModalContext } from "angular2-modal/plugins/bootstrap";
import { Observable } from "rxjs/Rx";

import { Message } from "../server/messages/general/";
import { childErrors, CustomValidators } from "../support/validators";
import { AuthService } from "./auth.service";
import { UserService } from "./identity/user.service";

export interface SecurePromptsContext {
  enforced : boolean;
  minimumPrompts? : number;
}

@Component({
  template: `
  <div class="modal-header">
    <button
      type="button"
      class="close"
      (click)="dialog.dismiss()"
      aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
    <h4 class="modal-title">
      Change Secure Prompts
    </h4>
  </div>
  <div class="modal-body">
    <p *ngIf="enforced" class="text-info">
      The application requires you to change your secure prompts before
      continuing.
    </p>
    <p>
      Secure prompts are question and answer pairs the system may use to verify
      your identity, for example, when resetting your password. You are
      encouraged to treat these fields as additional passwords in the sense that
      they should not be guessable. They are also stored in the same manner as
      passwords, meaning it is <em>impossible</em> for staff to look them up and
      see the values you have entered.
    </p>
    <p>
      You must enter at least <strong>{{minimumPrompts}}</strong> prompt pairs
      because that is the minimum security requirement of at least one of your
      zones. However you may enter more, if you prefer your account to have
      greater security.
    </p>
    <form [formGroup]="form" (submit)="submit()" class="untyped-statuses">
      <div *ngFor="let prompt of prompts.controls; let i = index" class="row">
        <div class="col-sm-6">
          <label>{{i+1 | ordinal}} Prompt</label>
          <input
            type="text"
            class="form-control"
            [formControl]="prompt.get('question')">
        </div>
        <div class="col-sm-6">
          <label>{{i+1 | ordinal}} Answer</label>
          <input
            type="text"
            class="form-control"
            [formControl]="prompt.get('answer')">
        </div>
      </div>
      <br>
      <button
        type="button"
        class="btn btn-default form-control"
        (click)="addPrompt()">
        Add a {{prompts.length + 1 | ordinal}} Prompt
      </button>
    </form>
  </div>

  <div class="modal-footer">
    <button
      *ngIf="!enforced"
      type="button"
      class="btn btn-default"
      (click)="dialog.dismiss()">
      Cancel
    </button>
    <button
      type="button"
      class="btn btn-primary"
      (click)="submit()">
      Submit
    </button>
  </div>
  `,
  styles: [ ]
})
export class ChangeSecurePromptsModalComponent
  implements ModalComponent<SecurePromptsContext>, OnInit
{
  public prompts : FormArray;
  public form : FormGroup;

  public minimumPrompts : number = 1;
  public enforced : boolean = false;

  constructor (
    public dialog : DialogRef<SecurePromptsContext>,
    public auth : AuthService,
    public user : UserService
  ) {}

  public ngOnInit () : void
  {
    this.minimumPrompts = (this.dialog.context.minimumPrompts
      || this.user.self.minumumSecurePrompts);
    this.enforced = !!this.dialog.context.enforced;

    this.prompts = new FormArray([]);
    for (let i : number = 0; i < this.minimumPrompts; i++)
    {
      this.addPrompt();
    }
    this.form = new FormGroup({
      prompts: this.prompts
    });
  }

  public addPrompt () : void
  {
    this.prompts.push(
      new FormGroup({
        question: new FormControl(null, Validators.required),
        answer: new FormControl(null, Validators.required)
      })
    );
  }

  public submit () : void
  {
    if (!this.form.valid)
    {
      if (this.prompts.controls.some(
        (prompt : FormGroup) => prompt.getError("required", ["question"])
          || prompt.getError("required", ["answer"])))
      {
        return alert("You must fill out all of the prompt fields!");
      }
      console.warn("Encountered errors:", childErrors(this.form));
      return alert("There are errors with the form.");
    }

    this.auth.changePrompts(
      this.prompts.value
    ).subscribe((result : Message) => {
        if ((result as any).ok !== false)
        {
          this.dialog.close(result);
        }
        else
        {
          alert("Your prompts could not be changed.");
          this.dialog.dismiss();
        }
      });
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/change-secure-prompts-modal.component.ts